import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import "./App.css";
import Footer from "./components/Footer";
import Header from "./components/Header";
import HeaderLoggedIn from "./components/Header/HeaderLoggedIn";
import ProtectedRoute from "./components/ProtectedRoute";
import axios from "./config/axios";
import AboutUs from "./pages/AboutUs";
import Admin from "./pages/Admin";
import AuthLogin from "./pages/AuthLogin";
import AuthRegister from "./pages/AuthRegister";
import ChangePassword from "./pages/ChangePassword";
import ContactUs from "./pages/ContactUs";
import CookiesPolicy from "./pages/CookiesPolicy";
import CurrentBalance from "./pages/CurrentBalance";
import Dashboard from "./pages/Dashboard";
import DataProtection from "./pages/DataProtection";
import EmailVerification from "./pages/EmailVerification";
import FAQs from "./pages/FAQs";
import ForgotPassword from "./pages/ForgotPassword";
import GrievancePolicy from "./pages/GrievancePolicy";
import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import MyRewards from "./pages/MyRewards";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Profile from "./pages/Profile";
import Projects from "./pages/Projects";
import ResetPassword from "./pages/ResetPassword";
import Rewards from "./pages/Rewards";
import SurveyProfiler from "./pages/SurveyProfiler";
import SurveyProfilerCategories from "./pages/SurveyProfilerCategories";
import SurveyProfilerCategoryQuestions from "./pages/SurveyProfilerCategoryQuestions";
import TermsOfServices from "./pages/TermsOfServices";
import WhyJoinUs from "./pages/WhyJoinUs";
import "./styles/design-system.css";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (token && storedUser) {
      // Don't set user from cache - wait for database verification
      // This prevents showing cached data for deleted users
      
      // Verify token and get fresh user data from database
      axios
        .get(`/users/profile`)
        .then((response) => {
          // User exists in database
          if (response.data && response.data._id) {
            setUser(response.data);
            localStorage.setItem("user", JSON.stringify(response.data));
          } else {
            throw new Error('User not found in database');
          }
        })
        .catch((error) => {
          console.error("❌ User validation failed - User does not exist in database:", error);
          // Clear all authentication data
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          sessionStorage.clear();
          setUser(null);
          // Redirect to login
          window.location.href = '/auth/login';
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    // Store user data in localStorage
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    // Clear all localStorage items
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Also clear sessionStorage if used
    sessionStorage.clear();
    // Force reload to clear any cached state
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {/* Conditionally render Header based on user login status */}
        {user ? (
          <HeaderLoggedIn user={user} onLogout={handleLogout} />
        ) : (
          <Header onLoginClick={() => (window.location.href = "/auth/login")} />
        )}

        <main className="App-main">
          <Routes>
            <Route path="/" element={<Home user={user} />} />

            {/* Public Pages */}
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/why-join-us" element={<WhyJoinUs />} />
            <Route path="/grievance-policy" element={<GrievancePolicy />} />
            <Route path="/cookies-policy" element={<CookiesPolicy />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/data-protection" element={<DataProtection />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsOfServices />} />

            {/* Authentication routes - separate from main website */}
            <Route
              path="/auth/login"
              element={<AuthLogin onLogin={handleLogin} />}
            />
            <Route
              path="/auth/register"
              element={<AuthRegister onLogin={handleLogin} />}
            />
            <Route
              path="/auth/verify-email"
              element={<EmailVerification onLogin={handleLogin} />}
            />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* Protected Routes - Validate token on every access */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute user={user}>
                  <Dashboard user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey-profiler"
              element={
                <ProtectedRoute user={user}>
                  <SurveyProfilerCategories user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey-profiler/all"
              element={
                <ProtectedRoute user={user}>
                  <SurveyProfiler user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey-profiler/:category"
              element={
                <ProtectedRoute user={user}>
                  <SurveyProfilerCategoryQuestions user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey-profiler/:category/:subcategory"
              element={
                <ProtectedRoute user={user}>
                  <SurveyProfilerCategoryQuestions user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute user={user}>
                  <Projects user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rewards"
              element={
                <ProtectedRoute user={user}>
                  <Rewards user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute user={user}>
                  <Profile user={user} setUser={setUser} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-rewards"
              element={
                <ProtectedRoute user={user}>
                  <MyRewards user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/current-balance"
              element={
                <ProtectedRoute user={user}>
                  <CurrentBalance user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute user={user}>
                  <ChangePassword user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                user?.role === "admin" ? (
                  <ProtectedRoute user={user}>
                    <Admin user={user} />
                  </ProtectedRoute>
                ) : (
                  <Navigate to="/" />
                )
              }
            />

            {/* Catch all route - 404 Page */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
